import { format, isValid, parse } from 'date-fns';
import React, { useRef } from 'react';
import DatePicker from 'react-datepicker';
import WindowedSelect, {
  components,
  createFilter,
  OptionProps,
} from 'react-windowed-select';
import { SingleFilterClause } from 'store/composableFiltersSlice';

export const ValueField = ({
  selectOptions,
  filterState,
  handleUpdateValueField,
  placement,
  matchSegmentOnBlur = true,
}: {
  selectOptions: any;
  filterState: SingleFilterClause;
  handleUpdateValueField: Function;
  placement: 'single' | 'group';
  matchSegmentOnBlur?: boolean;
}) => {
  const fieldWidth = placement === 'single' ? 'w-72' : 'w-52';
  const multiSelectRef = useRef(null);

  const selectBaseStyles = {
    input: (base: any) => ({
      ...base,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
    control: (base: any) => ({
      ...base,
      backgroundColor: '#F8F8F8',
      border: 'none',
      borderRadius: '8px',
      minHeight: '48px',
      padding: '4px',
    }),
    indicatorSeparator: () => ({}),
    multiValue: (base: any) => ({
      ...base,
      padding: '2px 4px',
      borderRadius: '4px',
    }),
    valueContainer: (base: any) => ({
      ...base,
      // maxHeight: '150px',
    }),
    groupHeading: (base: any, state: any) => {
      const optionsLength = state?.options?.length;

      return {
        ...base,
        textTransform: 'capitalize',
        fontSize: '16px',
        color: 'black',
        cursor: optionsLength > 100 ? 'not-allowed' : 'default',
      };
    },
    group: (base: any, state: any) => {
      const optionsLength = state?.options?.length;
      return {
        ...base,

        cursor: optionsLength > 100 ? 'not-allowed' : 'default',
        opacity: optionsLength > 100 && 0.5,
        // borderBottom: '1px solid #F1F1F1',
      };
    },
    option: (base: any) => {
      return {
        ...base,
        '&:hover': {
          backgroundColor: '#DEEBFF',
        },
        backgroundColor: '',
        borderBottom: '1px solid #F1F1F1',
      };
    },
  };

  // Do not show value field if operator is Empty or notEmpty
  const isOperatorWithoutValue =
    filterState.operator?.name &&
    ['empty', 'not_empty'].includes(
      filterState.operator.name.toLocaleLowerCase()
    );

  if (isOperatorWithoutValue) {
    return null;
  }

  if (selectOptions.type === 'amount') {
    return (
      <input
        type="number"
        value={filterState.value}
        className={`py-1.5 border-gray-300 rounded-md disabled:opacity-50 ${fieldWidth} h-12`}
        disabled={!!!filterState.operator?.name}
        onChange={(e) => {
          const amount = e.target.value ? Number(e.target.value) : '';

          handleUpdateValueField(amount);
        }}
      />
    );
  }

  if (selectOptions.type === 'date') {
    return (
      <div>
        <DatePicker
          className={`py-1.5 border-gray-300 rounded-md ${fieldWidth} h-12`}
          selected={
            !!!filterState.value
              ? null
              : parse(String(filterState.value), 'yyyy-MM-dd', Date.now())
          }
          dateFormat="yyyy-MM-dd"
          placeholderText="YYYY-MM-DD"
          disabled={!!!filterState.operator?.name}
          maxDate={new Date()}
          onChange={(e) => {
            if (e && isValid(e as Date)) {
              handleUpdateValueField(format(e as Date, 'yyyy-MM-dd'));
            } else {
              handleUpdateValueField('');
            }
          }}
        />
      </div>
    );
  }
  if (
    selectOptions.type === 'boolean' ||
    selectOptions.type === 'single-select'
  ) {
    let values;

    if (Array.isArray(filterState.value)) {
      values = filterState.value.map((item) => ({
        value: item.value,
        label: item.label || item.value,
      }));
    } else {
      values = {
        value: filterState.value,
        label: filterState.value,
      };
    }
    return (
      <WindowedSelect
        key={String(filterState.value)}
        //@ts-ignore
        value={filterState.value && values}
        options={selectOptions.values}
        className={fieldWidth}
        styles={selectBaseStyles}
        isDisabled={!!!filterState.operator?.name}
        onChange={(selectedOption: any) => {
          handleUpdateValueField(String(selectedOption?.value));
        }}
      />
    );
  }

  if (selectOptions.type === 'multi-select') {
    let values;

    if (Array.isArray(filterState.value)) {
      values = filterState.value.map((item) => ({
        value: item.value,
        label: item.label || item.value,
      }));
    } else {
      values = {
        value: filterState.value,
        label: filterState.value,
      };
    }

    const Option = ({ children, ...props }: OptionProps) => {
      const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
      const newProps = Object.assign(props, { innerProps: rest });
      return <components.Option {...newProps}>{children}</components.Option>;
    };

    const MIN_INPUT_LENGTH = 3;

    const filterOption = (candidate: any, input: string) => {
      if (input.length === 0) {
        return true;
      }

      if (input.length > 0 && input.length < MIN_INPUT_LENGTH) {
        return false;
      }

      return createFilter({ ignoreAccents: false })(candidate, input);
    };

    const noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
      return inputValue?.length >= MIN_INPUT_LENGTH
        ? 'No options'
        : 'Search input must be at least 3 characters';
    };

    const Group = (props: any) => {
      const optionslength = props?.options?.length;

      const onClick = () => {
        if (optionslength > 100) return;
        props.selectProps.onChange(props.options, 'select-option');
        // @ts-ignore
        multiSelectRef.current?.blur();
      };

      return (
        <components.Group
          {...props}
          headingProps={{ ...props.headingProps, optionslength, onClick }}
        />
      );
    };

    const groups = [
      {
        label: 'Select all',
        options: [...selectOptions.values],
      },
    ];

    const ValueContainer = (props: any) => {
      let length = props.getValue().length;

      return (
        <components.ValueContainer {...props}>
          {length > 5 ? (
            <>
              {props.children[0].slice(0, 5)}
              {!props.selectProps.menuIsOpen && `+ ${length - 1} items`}
              {React.cloneElement(props.children[1])}
            </>
          ) : (
            <>{props.children}</>
          )}
        </components.ValueContainer>
      );
    };

    const GroupHeading = (props: any) => {
      const options = props.optionslength;

      return (
        <div className="flex justify-between">
          <components.GroupHeading {...props} />
          <p
            className={`px-4 ${
              options > 100 ? 'text-segments-danger ' : 'text-gray-700'
            }`}
          >
            <span>{options?.toLocaleString()}</span> / 100
          </p>
        </div>
      );
    };

    return (
      <WindowedSelect
        ref={multiSelectRef}
        windowThreshold={20}
        components={{ Group, ValueContainer, GroupHeading, Option }}
        value={filterState.value && values}
        options={groups}
        className={fieldWidth}
        isMulti
        isDisabled={!!!filterState.operator?.name}
        styles={selectBaseStyles}
        filterOption={filterOption}
        noOptionsMessage={noOptionsMessage}
        onChange={(selectedOption: any[]) => {
          handleUpdateValueField(
            selectedOption.map((item) => ({
              value: item.value,
              label: item.label,
            }))
          );
        }}
      />
    );
  }

  return (
    <WindowedSelect
      isDisabled
      className={fieldWidth}
      styles={selectBaseStyles}
    />
  );
};
